import { FC } from "react";

import { useTranslation } from "react-i18next";

import { getPhoneHref } from "./Footer.utilities";

import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Logo from "../Logo";
import FooterLinkGroup from "./components/FooterLinkGroup";

import { brandColorTokens } from "@constants";
import { footerLinkGroups, socialLinks } from "./Footer.constants";

const Footer: FC = () => {
  const { t } = useTranslation();

  const phone = t("global.contactInfo.phone");
  const email = t("global.contactInfo.email");
  const currentYear = new Date().getFullYear();

  return (
    <Box
      component="footer"
      data-cy="app-layout-footer"
      sx={{ bgcolor: "background.default", borderTop: 1, borderColor: "divider", pt: { xs: 5, md: 8 }, pb: 4 }}
    >
      <Container maxWidth="lg">
        <Grid container={true} spacing={{ xs: 4, md: 3 }}>
          <Grid size={{ xs: 12, md: 5 }}>
            <Logo />
            <Stack spacing={2} sx={{ mt: 3, color: "text.primary" }}>
              <Box>
                <Typography>{t("footerV2.openingHours.weekdays")}</Typography>
                <Typography>{t("footerV2.openingHours.friday")}</Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {t("footerV2.openingHours.holidays")}
                </Typography>
              </Box>
              <Box>
                <Typography>
                  {t("footerV2.contact.phoneLabel")}{" "}
                  <Link href={getPhoneHref(phone)} underline="hover">
                    {phone}
                  </Link>
                </Typography>
                <Typography>
                  {t("footerV2.contact.emailLabel")}{" "}
                  <Link href={`mailto:${email}`} underline="hover">
                    {email}
                  </Link>
                </Typography>
              </Box>
              <Typography dangerouslySetInnerHTML={{ __html: t("global.contactInfo.address") }} />
              <Typography>
                {t("footerV2.contact.cvrLabel")} {t("global.contactInfo.cvr")}
              </Typography>
            </Stack>
          </Grid>
          {footerLinkGroups.map((group) => (
            <Grid key={group.titleKey} size={{ xs: 12, sm: 4, md: "grow" }}>
              <FooterLinkGroup group={group} />
            </Grid>
          ))}
        </Grid>
        <Divider sx={{ mt: { xs: 5, md: 8 }, mb: 3 }} />
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column-reverse", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography variant="body2">{t("footerV2.copyright", { year: currentYear })}</Typography>
          <Stack direction="row" spacing={0.5} sx={{ ml: { xs: -1, sm: 0 } }}>
            {socialLinks.map(({ label, urlKey, icon: Icon }) => (
              <IconButton
                key={label}
                href={t(urlKey)}
                target="_blank"
                rel="noreferrer"
                aria-label={label}
                sx={{ color: "text.secondary", "&:hover": { color: brandColorTokens.text.linkHover } }}
              >
                <Icon />
              </IconButton>
            ))}
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
